import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  ElementRef,
  OnDestroy,
  PLATFORM_ID,
  afterNextRender,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { environment } from '../../../environments/environment';

const STORAGE_KEY = 'test_banner_closed';

@Component({
  selector: 'app-test-banner',
  standalone: true,
  imports: [LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './test-banner.component.html',
})
export class TestBannerComponent implements OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private resizeObserver: ResizeObserver | null = null;

  private readonly bannerRoot = viewChild<ElementRef<HTMLElement>>('bannerRoot');

  private readonly dismissed = signal(false);

  readonly enabled = !environment.production;

  readonly visible = computed(() => this.enabled && !this.dismissed());

  constructor() {
    afterNextRender(() => {
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }
      if (sessionStorage.getItem(STORAGE_KEY) === 'true') {
        this.dismissed.set(true);
      }
      this.syncBannerOffset();
      this.observeResize();
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (isPlatformBrowser(this.platformId)) {
      document.documentElement.style.removeProperty('--tfr-banner-offset');
    }
  }

  close(): void {
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.setItem(STORAGE_KEY, 'true');
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.dismissed.set(true);
    this.syncBannerOffset();
  }

  private observeResize(): void {
    if (!isPlatformBrowser(this.platformId) || typeof ResizeObserver === 'undefined') {
      return;
    }
    const el = this.bannerRoot()?.nativeElement;
    if (!el) {
      return;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.syncBannerOffset());
    this.resizeObserver.observe(el);
  }

  private syncBannerOffset(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const height = this.visible() ? (this.bannerRoot()?.nativeElement.offsetHeight ?? 0) : 0;
    document.documentElement.style.setProperty('--tfr-banner-offset', `${height}px`);
  }
}
